import { PLAYER_SEARCH_MAX_LENGTH, type PlayerSummary } from '@brush/api-client';
import { useDebouncedValue } from '@brush/ui';
import { Search, UserPlus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useRoomPlayerSearch } from '../../queries';
import { pickerState } from './pickerState';

/**
 * Finding one of the room's players and adding them to something, or deciding there isn't one.
 *
 * Searches card id, first name, last name, city and state together rather than offering a field
 * to search *in*: an operator reading a payout sheet has one string in front of them and does not
 * know which column it belongs to. Matching is a plain substring on any of the five, done by the
 * server over the room's players (performance rank 2): the picker used to hold the room's whole roster
 * to match against. What it shows from an answer is `pickerState`'s decision.
 *
 * Lives above whatever it is adding to, not inside a row of it — a dropdown inside a scrolling
 * grid is clipped by the grid, and the search is one control used repeatedly rather than one per
 * row. It clears and keeps focus after each pick, which is the fast path for typing out a sheet.
 *
 * The "not one of these" answer is a first-class result, not a dead end: a results sheet
 * routinely names somebody the room has never carded, and sending the operator elsewhere to
 * create them would lose the rows already typed.
 */
export function PlayerPicker({
  roomId,
  excludeIds = [],
  onPick,
  onCreateNew,
  placeholder = 'Add a player — card ID, name, city…',
}: {
  roomId: string;
  /** Already on the sheet. Left out of the results rather than offered and then refused. */
  excludeIds?: string[];
  onPick: (player: PlayerSummary) => void;
  /** Taken when nothing matches, carrying whatever was typed so far. */
  onCreateNew: (term: string) => void;
  placeholder?: string;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The server is asked once typing pauses, and only while the list is open.
  const settled = useDebouncedValue(term, 250);
  const search = useRoomPlayerSearch(roomId, settled, open);
  const { status, available: matches, alreadyAdded } = pickerState({
    term,
    found: search.data,
    excludeIds,
    pending: settled.trim() !== term.trim() || search.isPlaceholderData,
    failed: search.isError,
  });

  // A click on an option fires after the input's blur, so closing is deferred long enough for the
  // option's own handler to run.
  const closeSoon = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    if (blurTimer.current !== undefined) clearTimeout(blurTimer.current);
  };

  const after = () => {
    setTerm('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-800/40" />
      <input
        ref={inputRef}
        value={term}
        maxLength={PLAYER_SEARCH_MAX_LENGTH}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          cancelClose();
          setOpen(true);
        }}
        onBlur={closeSoon}
        placeholder={placeholder}
        aria-label="Find a player by card ID, name, city or state"
        className="w-full rounded-lg border border-ink-800/15 py-2 pl-10 pr-3 text-sm outline-none focus:border-felt-500"
      />

      {open ? (
        <div
          onMouseDown={cancelClose}
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-ink-800/15 bg-white shadow-lg"
        >
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onPick(p);
                after();
              }}
              className="block w-full px-3 py-1.5 text-left hover:bg-felt-50"
            >
              <span className="block truncate text-sm text-ink-950">{p.name}</span>
              <span className="block truncate text-[11px] text-ink-800/60">
                {p.cardId}
                {p.city ? ` · ${p.city}` : ''}
                {p.state ? `, ${p.state}` : ''}
              </span>
            </button>
          ))}

          {status === 'searching' ? (
            <p className="px-3 py-2 text-xs text-ink-800/50">Searching…</p>
          ) : status === 'failed' ? (
            <p className="px-3 py-2 text-xs text-red-700">Couldn't search the room's players. Try again.</p>
          ) : matches.length === 0 && alreadyAdded.length === 0 ? (
            <p className="px-3 py-2 text-xs text-ink-800/50">No player matches that.</p>
          ) : null}

          {alreadyAdded.map((p) => (
            <p key={p.id} className="px-3 py-1.5 text-xs text-ink-800/45">
              <span className="line-through">{p.name}</span> — already on this sheet
            </p>
          ))}

          <button
            type="button"
            onClick={() => {
              onCreateNew(term);
              after();
            }}
            className="flex w-full items-center gap-2 border-t border-ink-800/10 px-3 py-2 text-left text-xs font-medium text-felt-700 hover:bg-felt-50"
          >
            <UserPlus className="size-3.5" /> Not listed — add a new player
          </button>
        </div>
      ) : null}
    </div>
  );
}
